import { useCallback } from 'react';

import { SEARCH_CACHE_SECONDS, SEARCH_LOCATION_COUNTRY_CODE_FILTER } from '#src/app/pages/search/shared/consts';

import { useSearchClient } from '../../../../../useSearchClient';
import { useSearchIndexName } from '../../../../../useSearchIndexName';

// more than the number of countries, so every country with petitions is returned
const MAX_COUNTRIES = 300;

type Result = ModelHookResult<
	undefined,
	{
		// resolves to undefined when the counts can't be fetched, so callers can skip count-based ranking
		getCountryPetitionCounts: () => Promise<Readonly<Record<string, number>> | undefined>;
	}
>;

export function useCountryPetitionCounts(): Result {
	const {
		data: { typesenseClient },
	} = useSearchClient();

	const {
		data: { searchIndexName },
	} = useSearchIndexName();

	return {
		actions: {
			getCountryPetitionCounts: useCallback(async () => {
				try {
					const response = await typesenseClient.collections(searchIndexName).documents().search(
						{
							q: '*',
							query_by: '*',
							facet_by: SEARCH_LOCATION_COUNTRY_CODE_FILTER,
							max_facet_values: MAX_COUNTRIES,
							per_page: 0,
						},
						{
							// identical request every keystroke, so it's served from the client cache
							cacheSearchResultsForSeconds: SEARCH_CACHE_SECONDS,
						},
					);
					const facetCount = response.facet_counts?.find(
						(facet) => facet.field_name === SEARCH_LOCATION_COUNTRY_CODE_FILTER,
					);
					if (!facetCount?.counts) return undefined;
					return Object.fromEntries(facetCount.counts.map(({ value, count }) => [value, count]));
				} catch {
					return undefined;
				}
			}, [searchIndexName, typesenseClient]),
		},
	};
}
